import { Box } from '@mui/material';
import PricingTable from 'src/components/table/PricingTable';
import { useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/store/store';
import { Typography } from 'src/components/new/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { updateUnsavedPostageRates } from 'src/features/admin/adminSlice';
import PostageRateInput from '../pricing-inputs/PostageRateInput';
import { formatPrice, validateInput } from '../utils/utils';

const RATE_FIELDS = [
  'first_class_domestic',
  'first_class_canada',
  'first_class_mexico',
  'first_class_international',
] as const;

const THIRD_ROW_COLLAPSABLE_WEIGHTS = new Set(['5', '6', '7', '8', '9', '10', '11', '12', '13']);

export const mapPostageType = {
  '6x9': '6x9',
  '#10': '#10',
  flat: 'Flat',
  flat_rate_envelope: 'Flat Rate Envelope',
  legal_flat_rate_envelope: 'Legal Flat Rate Envelope',
  flat_rate_box: 'Flat Rate Box',
};

interface RateCell {
  id: number;
  price: number;
}

interface PostageRow {
  weight: string;
  packageType: string;
  sheetRange: React.ReactNode;
  hasBottomBorder?: boolean;
  [key: string]: any;
}

const columns = [
  {
    headerName: (
      <>
        <p style={{ margin: 0 }}>Weight</p>
        <p style={{ margin: 0, color: '#7e828b' }}>Not Over (oz)</p>
      </>
    ),
    field: 'weight',
    width: 70,
  },
  { headerName: 'Sheet Range', field: 'sheetRange', width: 50 },
  {
    headerName: 'Package',
    field: 'packageType',
    width: 120,
    customHeaderStyle: { borderRight: '1px solid rgba(224,224,224,1)' },
    customCellStyle: { borderRight: '1px solid rgba(224,224,224,1)' },
  },
  ...RATE_FIELDS.map((field) => ({
    headerName: capitalize(field.replace('first_class_', '').replace('_', ' ')),
    field,
    width: 120,
    customHeaderStyle: { textAlign: 'center' as const },
  })),
];

const FirstClassPostageRates: React.FC<{ isEditingEnabled: boolean }> = ({ isEditingEnabled }) => {
  const [isSecondRowCollapsed, setIsSecondRowCollapsed] = useState(false);
  const [isThirdRowCollapsed, setIsThirdRowCollapsed] = useState(false);

  const postageRates = useSelector((state: RootState) => state.admin.sortedPostageRatesV2);
  const unsavedPostageRates = useSelector((state: RootState) => state.admin.unsavedPostageRates);
  const dispatch = useDispatch<AppDispatch>();

  const changedIds = useMemo(() => new Set(Object.keys(unsavedPostageRates).map(Number)), [unsavedPostageRates]);

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, id: number, packageType: string, mailingType: string) => {
      const isValidInput = validateInput(e.target.value, 2);
      if (!isValidInput) return;
      dispatch(
        updateUnsavedPostageRates({
          id,
          value: formatPrice(e.target.value, 2),
          packageType,
          mailingType,
        }),
      );
    },
    [dispatch],
  );

  const data: PostageRow[] = useMemo(() => {
    let rows = Object.entries(postageRates.first_class ?? {})
      .map(([weight, rates]: any): PostageRow | null => {
        const packageType = rates[0].packageType;

        if (
          (weight !== '15' && packageType === 'flat_rate_envelope') ||
          (weight !== '22' && packageType === 'legal_flat_rate_envelope')
        ) {
          return null;
        }

        const row: PostageRow = {
          weight,
          packageType,
          sheetRange: getSheetRange(weight, isSecondRowCollapsed, isThirdRowCollapsed, packageType),
          hasBottomBorder: shouldHaveBorder(weight, isSecondRowCollapsed, isThirdRowCollapsed, packageType),
        };

        rates.forEach((r: any) => {
          if (r.packageType === packageType) {
            row[r.mailingType] = { id: r.id, price: r.price };
          }
        });

        row.weight = getDisplayWeight(weight, isSecondRowCollapsed, isThirdRowCollapsed, packageType);

        return row;
      })
      .filter(Boolean) as PostageRow[];

    if (!isSecondRowCollapsed) {
      rows = rows.filter((r) => r.weight !== '3');
    }

    if (!isThirdRowCollapsed) {
      rows = rows.filter((r) => !THIRD_ROW_COLLAPSABLE_WEIGHTS.has(r.weight));
    }

    return rows;
  }, [postageRates, isSecondRowCollapsed, isThirdRowCollapsed]);

  const renderCell = (row: PostageRow, column: any) => {
    if (RATE_FIELDS.includes(column.field)) {
      const cell: RateCell = row[column.field];
      return (
        <PostageRateInput
          isEditingEnabled={isEditingEnabled}
          onInputChange={onInputChange}
          isDirty={changedIds.has(cell.id)}
          packageType={row.packageType}
          mailingType={column.field}
          id={cell.id}
          value={unsavedPostageRates[cell.id] ?? `$${(cell?.price ?? 0).toFixed(2)}`}
        />
      );
    }

    if (column.field === 'packageType') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ p: 1, borderRadius: '999px', backgroundColor: '#f0f6ff' }}>
            <Typography fontWeight={500} fontSize={14}>
              {mapPostageType[row.packageType as keyof typeof mapPostageType]}
            </Typography>
          </Box>

          {(row.weight === '2 - 3' || row.weight === '2') &&
            (isSecondRowCollapsed ? (
              <KeyboardArrowUpIcon onClick={() => setIsSecondRowCollapsed(false)} sx={{ cursor: 'pointer' }} />
            ) : (
              <KeyboardArrowDownIcon onClick={() => setIsSecondRowCollapsed(true)} sx={{ cursor: 'pointer' }} />
            ))}

          {(row.weight === '4 - 13' || row.weight === '4') &&
            (isThirdRowCollapsed ? (
              <KeyboardArrowUpIcon onClick={() => setIsThirdRowCollapsed(false)} sx={{ cursor: 'pointer' }} />
            ) : (
              <KeyboardArrowDownIcon onClick={() => setIsThirdRowCollapsed(true)} sx={{ cursor: 'pointer' }} />
            ))}
        </Box>
      );
    }

    return row[column.field];
  };

  return <PricingTable mainHeaderName="First Class" renderCell={renderCell} data={data} columns={columns} />;
};

export default FirstClassPostageRates;

const getDisplayWeight = (weight: string, secondCollapsed: boolean, thirdCollapsed: boolean, type: string) => {
  if (weight === '2' && !secondCollapsed) return '2 - 3';
  if (weight === '4' && !thirdCollapsed) return '4 - 13';
  if (type === 'flat_rate_envelope') return '13 - 21';
  if (type === 'legal_flat_rate_envelope') return '21 - 24';
  return weight;
};

const getSheetRange = (weight: string, secondCollapsed: boolean, thirdCollapsed: boolean, type?: string) => {
  if (type === 'flat_rate_envelope')
    return (
      <>
        73 → <b>120</b>
      </>
    );
  if (type === 'legal_flat_rate_envelope')
    return (
      <>
        121 → <b>140</b>
      </>
    );

  switch (weight) {
    case '1':
      return (
        <>
          1 → <b>5</b>
        </>
      );
    case '2':
      return secondCollapsed ? (
        '6 →'
      ) : (
        <>
          6 → <b>16</b>
        </>
      );
    case '3':
      return <b>16</b>;
    case '4':
      return thirdCollapsed ? (
        '17 →'
      ) : (
        <>
          17 → <b>72</b>
        </>
      );
    case '13':
      return <b>72</b>;
    case '92':
      return (
        <>
          141 → <b>500</b>
        </>
      );
    default:
      return '-';
  }
};

const shouldHaveBorder = (weight: string, second: boolean, third: boolean, type: string) => {
  if (['flat_rate_envelope', 'legal_flat_rate_envelope', 'flat_rate_box'].includes(type)) return true;
  if (weight === '1' || weight === '3' || weight === '13') return true;
  if (weight === '2' && !second) return true;
  if (weight === '4' && !third) return true;
  return false;
};

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
